import { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { Intent } from './intent';
import { User } from './user-model';

export type SubscriptionPeriod = 'monthly' | 'annual';

/**
 * The plan ids that can be subscribed to. Must match SUBSCRIPTION_PLANS in
 * server/src/constants.ts.
 */
export const subscriptionPlanIds = ['indie', 'pro', 'scale'] as const;

export type SubscriptionPlanId = (typeof subscriptionPlanIds)[number];

/**
 * The prepaid pack ids. Must match REQUEST_PACKS in server/src/constants.ts.
 */
export const requestPackIds = ['pack_100k', 'pack_500k', 'pack_2m'] as const;

export type RequestPackId = (typeof requestPackIds)[number];

export type SubscriptionPlan = {
  id: string;
  name: string;
  icon: IconDefinition;
  banner?: string;

  // Picks the plan's colour as well as its buttons'
  buttonIntent?: Intent;

  // Drawn filled and tinted, to stand out from the others
  highlighted?: boolean;
  monthlyPrice: string | null;
  annualPrice: string | null;
  tagline?: string;
  features: string[];
};

export type RequestPack = {
  id: RequestPackId;
  name: string;
  requests: number;
  price: string;
  perThousand: string;
};

export type Subscription = {
  id: string;
  createdAt: Date;
  updatedAt: Date;
  user?: User;
  plan: {
    id: string;
    name: string;
    monthlyPrice: number;
    annualPrice: number;
    rateLimit: number | null;
    maxRequestsPerMinute: number | null;
    maxRequestsPerMonth: number | null;
    overdraftPercent: number;
    maxStorageBytes: number | null;
    maxLists: number | null;
    maxItemsPerList: number | null;
    maxIndexesPerList: number | null;
    maxItemSize: number | null;
    maxItemVersions: number | null;
    maxTokens: number | null;
    maxIdentities: number | null;
    maxRealtimeConnections: number | null;
    maxWebhooks: number | null;
    maxFlows: number | null;
    generativeAPI: boolean;
  };
  period: SubscriptionPeriod | null;
  currentPeriodEndAt: Date | null;
  customerId?: string | null;
  subscriptionId?: string | null;
  lastInvoiceId?: string | null;
  lastPaymentSucceededAt?: Date | null;
  lastPaymentFailedAt?: Date | null;
  activated?: boolean;
};

export type SubscriptionStats = {
  maxRequestsPerMinute: number;
  totalLists: number;
  maxItemsPerList: number;
  maxIndexesPerList: number;
  maxItemSize: number;
  totalTokens: number;
  totalIdentities?: number;
};

/**
 * A user's request and storage usage for the current calendar month
 */
export type SubscriptionUsage = {
  periodStart: string;
  periodEnd: string;
  requestCount: number;
  blockedCount: number;
  requestAllowance: number | null;
  requestsRemaining: number | null;
  overdraftAllowance: number;
  credits: number;
  storageBytes: number;
  storageAllowance: number | null;
};

export type SubscriptionPricesResponse = Record<
  string,
  {
    plan: string;
    period: SubscriptionPeriod;
    amount: number;
  }
>;

export type SubscriptionPacksResponse = Record<
  string,
  {
    pack: string;
    requests: number;
    amount: number;
  }
>;

export type UserSubscriptionForAdmin = {
  subscription: Subscription;
  stats?: SubscriptionStats;
  stripeSubscriptions?: any;
};

export type CurrentUserSubscription = {
  subscription: Subscription;
  stats: SubscriptionStats;
  usage: SubscriptionUsage;
};

export type SubscribeResponse = {
  planId: string;
  period: SubscriptionPeriod;
  amount: number;
  checkoutUrl: string;
};

export type PackCheckoutResponse = {
  packId: string;
  requests: number;
  amount: number;
  checkoutUrl: string;
};

export type PortalResponse = {
  portalUrl: string;
};

export const SubscriptionPlanQueryParam = {
  encode: (value?: string | null): string | undefined =>
    value ? (value as string) : undefined,

  decode: (value?: string | (string | null)[] | null): string | undefined =>
    value && (subscriptionPlanIds as readonly string[]).includes(value as any)
      ? value.toString()
      : undefined,
};

export const SubscriptionPeriodQueryParam = {
  encode: (value?: SubscriptionPeriod | null): string | undefined =>
    value ? (value as string) : undefined,

  decode: (
    value?: string | (string | null)[] | null
  ): SubscriptionPeriod | undefined =>
    value && ['monthly', 'annual'].includes(value as any)
      ? (value as SubscriptionPeriod)
      : undefined,
};

export const subscriptionOrderFields = [
  'createdAt',
  'updatedAt',
  'userId',
  'planId',
  'period',
  'customerId',
  'subscriptionId',
  'lastInvoiceId',
  'lastPaymentSucceededAt',
  'lastPaymentFailedAt',
  'currentPeriodEndAt',
  'activated',
] as const;

export type SubscriptionOrderField = (typeof subscriptionOrderFields)[number];
